/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import real from './../../../../float64/real';
import imag from './../../../../float64/imag';
import Float64Array from '@stdlib/array/float64';
import Complex128 from './../../../../float64/ctor';
import assign from './assign.js';


// VARIABLES //

var out = new Float64Array( 2 );


// MAIN //

/**
* Divides two double-precision complex floating-point numbers.
*
* ## References
*
* -   Baudin, Michael, and Robert L. Smith. 2012. "A Robust Complex Division in Scilab." _arXiv_ abs/1210.4539 \[cs.MS\] (October): 1–25. <https://arxiv.org/abs/1210.4539>.
*
* @param {Complex128} z1 - complex number
* @param {Complex128} z2 - complex number
* @returns {Complex128} result
*
* @example
* import Complex128 from '@stdlib/complex/float64/ctor';
*
* var z1 = new Complex128( -13.0, -1.0 );
* var z2 = new Complex128( -2.0, 1.0 );
*
* var out = cdiv( z1, z2 );
* // returns <Complex128>[ 5.0, 3.0 ]
*/
function cdiv( z1, z2 ) {
	var re1;
	var re2;
	var im1;
	var im2;

	re1 = real( z1 );
	re2 = real( z2 );
	im1 = imag( z1 );
	im2 = imag( z2 );

	out = assign( re1, im1, re2, im2, out, 1, 0 );

	return new Complex128( out[ 0 ], out[ 1 ] );
}


// EXPORTS //

export default cdiv;
